import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import { formatClubDateTime } from '../../utils/clubTime';
import CheckLengthBadge from './CheckLengthBadge';
import ExcludedLengthBadge from './ExcludedLengthBadge';
import CatchStatusLegend, { getCatchCardStatus } from './CatchStatusLegend';
import CatchDetailDialog from './CatchDetailDialog';
import ConfirmActionDialog from './ConfirmActionDialog';
import { computeRanks, isReviewed, type MysteryFish, checkedByLabel } from '@shared/adjudication';
import { catchRotation, rotatedFrameStyle, rotatedImageStyle } from '../../utils/catchRotation';

const client = generateClient({ authMode: 'userPool' });

const SPECIES_LABELS: Record<string, string> = {
  FLATHEAD: 'Flathead',
  BREAM: 'Bream',
  JEWFISH: 'Jewfish',
  MANGROVE_JACK: 'Mangrove Jack',
  WHITING: 'Whiting',
  TAILOR: 'Tailor',
  TREVALLY: 'Trevally',
  OTHER: 'Other',
};

const STATUS_COLOURS: Record<string, string> = {
  APPROVED: 'bg-green-100 text-green-800',
  DISQUALIFIED: 'bg-red-100 text-red-800',
  PENDING: 'bg-yellow-100 text-yellow-800',
};

interface SubmissionReviewProps {
  submissions: any[];
  eventId: string;
  onRefresh: () => void;
  /** The event's mystery fish, for the same fallback the leaderboard uses. */
  mystery?: MysteryFish | null;
  /** True once results are published: rotation is then refused. */
  published?: boolean;
}

const SubmissionReview: React.FC<SubmissionReviewProps> = ({ submissions, eventId, onRefresh, mystery, published }) => {
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [speciesFilter, setSpeciesFilter] = useState('ALL');
  const [sortBySuspicion, setSortBySuspicion] = useState(false);
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [reasonInput, setReasonInput] = useState<Record<string, string>>({});
  const [expandedAi, setExpandedAi] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [openCatchId, setOpenCatchId] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [pendingConfirm, setPendingConfirm] = useState<{ catchId: string; action: 'APPROVE' | 'DISQUALIFY'; reason?: string } | null>(null);
  const cardRefs = React.useRef<Record<string, HTMLButtonElement | null>>({});

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Load photo thumbnails
  useEffect(() => {
    const loadPhotos = async () => {
      const urls: Record<string, string> = {};
      for (const sub of submissions) {
        if (sub.photoS3Key && !photoUrls[sub.id]) {
          try {
            const result = await (client.queries as any).generateDownloadUrl({ s3Key: sub.photoS3Key });
            const data = parseLambdaResponse(result?.data);
            if (data?.downloadUrl) urls[sub.id] = data.downloadUrl;
          } catch { /* skip */ }
        }
      }
      if (Object.keys(urls).length > 0) {
        setPhotoUrls((prev) => ({ ...prev, ...urls }));
      }
    };
    loadPhotos();
  }, [submissions]);

  const ranks = computeRanks(submissions as any, mystery);

  const filtered = submissions.filter((s: any) => {
    const status = s.status || 'PENDING';
    if (statusFilter !== 'ALL' && status !== statusFilter) return false;
    if (speciesFilter !== 'ALL' && s.species !== speciesFilter) return false;
    return true;
  }).sort((a: any, b: any) => {
    if (sortBySuspicion) return (b.aiSuspicionScore || 0) - (a.aiSuspicionScore || 0);
    return 0;
  });

  const getAiBadge = (sub: any) => {
    if (sub.aiVerificationStatus === 'PENDING' || !sub.aiVerificationStatus) {
      return <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">AI Verifying…</span>;
    }
    if (sub.aiVerificationStatus === 'FAILED') {
      return <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500">AI Unavailable</span>;
    }
    const score = sub.aiSuspicionScore || 0;
    let colour = 'bg-green-100 text-green-800';
    if (score >= 60) colour = 'bg-red-100 text-red-800';
    else if (score >= 30) colour = 'bg-amber-100 text-amber-800';
    return <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${colour}`}>AI: {score}</span>;
  };

  const getAiFlags = (sub: any): Array<{ code: string; description: string; detail?: string }> => {
    try {
      const flags = typeof sub.aiFlags === 'string' ? JSON.parse(sub.aiFlags) : sub.aiFlags;
      return Array.isArray(flags) ? flags : [];
    } catch { return []; }
  };

  const handleAdjudicate = async (catchId: string, action: 'APPROVE' | 'DISQUALIFY', reasonOverride?: string) => {
    // Guards a double click: once a decision for this catch is in flight,
    // a second click is a no-op, not a second request.
    if (actionLoading === catchId) return;
    // A4: a decision made while results are published changes what the public
    // sees, so it is confirmed first. Approve is non-destructive (destructive=false).
    if (published && !pendingConfirm) {
      setPendingConfirm({ catchId, action, reason: reasonOverride });
      return;
    }
    setPendingConfirm(null);
    const sub = submissions.find((s: any) => s.id === catchId);
    setActionLoading(catchId);
    try {
      const disqualificationReason = action === 'DISQUALIFY' ? (reasonOverride ?? reasonInput[catchId] ?? '') : undefined;
      const result = await (client.mutations as any).adjudicateCatch({
        catchId,
        decision: action === 'APPROVE' ? 'APPROVED' : 'DISQUALIFIED',
        disqualificationReason,
        expectedStatus: sub?.status,
      });
      if (result?.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }
      // The Lambda reports business failures (catch already adjudicated, event
      // in the wrong state, missing disqualification reason, catch not found)
      // in its own payload with a 200 response. Checking result.errors alone
      // would therefore show "Submission approved" on a decision the Lambda
      // rejected, leaving the admin believing a catch was settled when its
      // status never changed — and on a disqualification, believing a fish was
      // thrown out when it is still live on the leaderboard.
      const data = parseLambdaResponse<{ success?: boolean; error?: string; message?: string }>(result?.data);
      if (!data?.success) {
        throw new Error(data?.message || data?.error || 'Failed to update submission');
      }
      showToast(`Submission ${action === 'APPROVE' ? 'approved' : 'disqualified'}`, 'success');
      // Close the detail dialog on a successful decision — the card it was
      // opened from is about to change state (checked/disqualified), so there
      // is nothing left to action in the dialog.
      setOpenCatchId((current) => (current === catchId ? null : current));
      onRefresh();
    } catch (err: any) {
      console.error('Error adjudicating catch:', err);
      const message = err.message || 'Failed to update submission';
      showToast(message, 'error');
      setStatusMessage(message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReinstate = async (catchId: string) => {
    if (actionLoading === catchId) return;
    const sub = submissions.find((s: any) => s.id === catchId);
    setActionLoading(catchId);
    try {
      const result = await (client.mutations as any).adjudicateCatch({
        actionType: 'REINSTATE_CATCH',
        catchId,
        expectedStatus: sub?.status || 'DISQUALIFIED',
      });
      if (result?.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }
      const data = parseLambdaResponse<{ success?: boolean; error?: string; message?: string }>(result?.data);
      if (!data?.success) {
        throw new Error(data?.message || data?.error || 'Failed to reinstate the catch');
      }
      showToast('Catch reinstated', 'success');
      setOpenCatchId((current) => (current === catchId ? null : current));
      onRefresh();
    } catch (err: any) {
      console.error('Error adjudicating catch:', err);
      const message = err.message || 'Failed to reinstate the catch';
      showToast(message, 'error');
      setStatusMessage(message);
    } finally {
      setActionLoading(null);
    }
  };

  const closeDialog = (catchId: string) => {
    setOpenCatchId(null);
    setTimeout(() => cardRefs.current[catchId]?.focus(), 0);
  };

  const openSub = openCatchId ? submissions.find((s: any) => s.id === openCatchId) : null;

  return (
    <div>
      {toast && (
        <div role="alert" aria-live="assertive" className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm ${
          toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
        }`}>
          {toast.message}
        </div>
      )}

      <div aria-live="polite" className="sr-only">{statusMessage}</div>

      {pendingConfirm && (
        <ConfirmActionDialog
          title="This changes the published results."
          message="Continue?"
          confirmLabel={pendingConfirm.action === 'APPROVE' ? 'Approve' : 'Disqualify'}
          destructive={pendingConfirm.action === 'DISQUALIFY'}
          onConfirm={() => handleAdjudicate(pendingConfirm.catchId, pendingConfirm.action, pendingConfirm.reason)}
          onCancel={() => setPendingConfirm(null)}
        />
      )}

      <div className="mb-4">
        <CatchStatusLegend />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-fishing-blue"
          aria-label="Filter by status"
        >
          <option value="ALL">All Statuses</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="DISQUALIFIED">Disqualified</option>
        </select>
        <select
          value={speciesFilter}
          onChange={(e) => setSpeciesFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-fishing-blue"
          aria-label="Filter by species"
        >
          <option value="ALL">All Species</option>
          {Object.entries(SPECIES_LABELS).map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>
        <span className="text-sm text-gray-500 self-center">{filtered.length} submissions</span>
        <button
          onClick={() => setSortBySuspicion(!sortBySuspicion)}
          className={`px-3 py-2 border rounded-md text-sm ${sortBySuspicion ? 'bg-fishing-blue text-white border-fishing-blue' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
        >
          {sortBySuspicion ? '⚠ Sorted by AI Score' : 'Sort by AI Score'}
        </button>
      </div>

      {/* Submission cards */}
      {filtered.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center text-gray-500">
          No submissions match the current filters.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((sub: any) => {
            const status = sub.status || 'PENDING';
            const cardStatus = getCatchCardStatus(sub, ranks[sub.id] ?? null);
            // Approve/Disqualify is offered whenever the catch is not disqualified
            // and not yet checked by a person. Catches are auto-approved
            // (status APPROVED on creation), so gating on status === 'PENDING'
            // would hide the actions on almost every card.
            const showActions = status !== 'DISQUALIFIED' && !isReviewed(sub);
            const showReinstate = status === 'DISQUALIFIED';
            const checkedLabel = isReviewed(sub)
              ? checkedByLabel(sub as any, formatClubDateTime)
              : 'Not checked';

            const openDetail = () => setOpenCatchId(sub.id);

            return (
              <div
                key={sub.id}
                className={`bg-white shadow rounded-lg overflow-hidden ${cardStatus.borderClass}`}
              >
                {/* Photo thumbnail: opens the catch detail (a real button, so the
                    Approve/Disqualify buttons on the card are not nested in it). */}
                <button
                  type="button"
                  ref={(el) => { cardRefs.current[sub.id] = el; }}
                  onClick={openDetail}
                  aria-label={`View catch: ${SPECIES_LABELS[sub.species] || sub.species}, ${sub.lengthCm} cm, ${sub.competitorName || 'Unknown'}`}
                  className="block w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-fishing-blue focus-visible:ring-inset"
                >
                  {photoUrls[sub.id] ? (
                    <div style={rotatedFrameStyle(catchRotation(sub))} className="w-full h-40 overflow-hidden">
                      <img
                        src={photoUrls[sub.id]}
                        alt={`Catch photo: ${SPECIES_LABELS[sub.species] || sub.species}, ${sub.lengthCm} cm`}
                        className="w-full h-40 object-cover"
                        style={rotatedImageStyle(catchRotation(sub))}
                      />
                    </div>
                  ) : (
                    <div className="w-full h-40 bg-gray-100 flex items-center justify-center text-gray-400 text-sm">
                      No photo
                    </div>
                  )}
                  <span className="block px-4 pt-2 text-left text-xs font-medium text-fishing-blue underline">View catch</span>
                </button>

                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1">
                      <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {SPECIES_LABELS[sub.species] || sub.species}
                      </span>
                      {sub.submissionSource && (
                        <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${sub.submissionSource === 'SMS' ? 'bg-purple-100 text-purple-800' : 'bg-blue-50 text-blue-600'}`}>
                          {sub.submissionSource}
                        </span>
                      )}
                    </div>
                    <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${STATUS_COLOURS[status] || STATUS_COLOURS.PENDING}`}>
                      {status}
                    </span>
                  </div>

                  <div className="text-sm font-semibold" style={{ color: cardStatus.state === 'disqualified' ? '#b91c1c' : cardStatus.state === 'needsReview' ? '#b45309' : cardStatus.state === 'checked' ? '#15803d' : '#4b5563' }}>
                    {cardStatus.label}
                  </div>

                  <div className="text-sm text-gray-900 font-medium">{sub.competitorName || 'Unknown'}</div>
                  <div className="text-sm text-gray-600">
                    {sub.lengthCm}cm <CheckLengthBadge catchItem={sub} className="ml-1 align-middle" />
                    <ExcludedLengthBadge catchItem={sub} className="ml-1 align-middle" />
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {sub.submissionTimestamp ? formatClubDateTime(sub.submissionTimestamp) : ''}
                  </div>
                  {sub.gpsVerified === false && (
                    <div className="text-xs text-gray-500 mt-1">GPS unverified</div>
                  )}
                  <div className="text-xs text-gray-600 mt-1">{checkedLabel}</div>

                  {/* AI Verification Badge */}
                  <div className="mt-2 flex items-center gap-2">
                    {getAiBadge(sub)}
                    {sub.aiVerificationStatus === 'COMPLETED' && getAiFlags(sub).length > 0 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setExpandedAi(prev => ({ ...prev, [sub.id]: !prev[sub.id] })); }}
                        className="text-xs text-fishing-blue hover:underline"
                      >
                        {expandedAi[sub.id] ? 'Hide details' : 'View flags'}
                      </button>
                    )}
                  </div>

                  {/* AI Flag Details (expandable) */}
                  {expandedAi[sub.id] && sub.aiVerificationStatus === 'COMPLETED' && (
                    <div className="mt-2 p-2 bg-gray-50 rounded text-xs space-y-1">
                      {getAiFlags(sub).map((flag, i) => (
                        <div key={i} className="flex items-start gap-1">
                          <span className="text-red-500">⚑</span>
                          <div>
                            <span className="font-medium">{flag.code}</span>: {flag.description}
                            {flag.detail && <span className="text-gray-500 ml-1">({flag.detail})</span>}
                          </div>
                        </div>
                      ))}
                      {sub.aiVerifiedAt && (
                        <div className="text-gray-400 mt-1">Verified: {formatClubDateTime(sub.aiVerifiedAt)}</div>
                      )}
                      <div className="text-gray-400 italic">Advisory only — admin makes all final decisions</div>
                    </div>
                  )}

                  {/* Actions */}
                  {showActions && (
                    <div className="mt-3 space-y-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        placeholder="Reason (required to disqualify)"
                        value={reasonInput[sub.id] || ''}
                        onChange={(e) => setReasonInput((prev) => ({ ...prev, [sub.id]: e.target.value }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-fishing-blue"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleAdjudicate(sub.id, 'APPROVE')}
                          disabled={actionLoading === sub.id}
                          className="flex-1 min-h-[44px] px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded disabled:bg-gray-300"
                        >
                          {actionLoading === sub.id ? '...' : 'Approve'}
                        </button>
                        <button
                          onClick={() => handleAdjudicate(sub.id, 'DISQUALIFY')}
                          disabled={actionLoading === sub.id}
                          className="flex-1 min-h-[44px] px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded disabled:bg-gray-300"
                        >
                          {actionLoading === sub.id ? '...' : 'Disqualify'}
                        </button>
                      </div>
                    </div>
                  )}
                  {showReinstate && (
                    <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleReinstate(sub.id)}
                        disabled={actionLoading === sub.id}
                        className="w-full min-h-[44px] px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded disabled:bg-gray-300"
                      >
                        {actionLoading === sub.id ? '...' : 'Reinstate'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {openSub && (
        <CatchDetailDialog
          catchItem={openSub}
          rank={ranks[openSub.id] ?? null}
          photoUrl={photoUrls[openSub.id] ?? null}
          onApprove={(catchId) => handleAdjudicate(catchId, 'APPROVE')}
          onDisqualify={(catchId, reason) => handleAdjudicate(catchId, 'DISQUALIFY', reason)}
          onReinstate={(catchId) => handleReinstate(catchId)}
          actionLoading={actionLoading === openSub.id}
          onClose={() => closeDialog(openSub.id)}
          onRotated={() => onRefresh()}
          competitionPublished={!!published}
        />
      )}
    </div>
  );
};

export default SubmissionReview;
